import { Select } from '@/components/forms/select'
import { IconButton } from '@/components/primitives/icon-button'
import { cn } from '@/lib/cn'

interface PaginationLabels {
  next: string
  pageSize: (size: number) => string
  previous: string
}

export interface PaginationProps {
  disabled?: boolean
  labels: PaginationLabels
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  page: number
  pageSize: number
  pageSizeOptions?: number[]
  total: number
}

type PaginationItem = number | 'ellipsis'

function getPaginationItems(currentPage: number, pageCount: number): PaginationItem[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1)
  }

  const items = new Set<number>([1, pageCount, currentPage])

  if (currentPage > 1) {
    items.add(currentPage - 1)
  }
  if (currentPage < pageCount) {
    items.add(currentPage + 1)
  }
  if (currentPage <= 3) {
    items.add(2)
    items.add(3)
    items.add(4)
  }
  if (currentPage >= pageCount - 2) {
    items.add(pageCount - 1)
    items.add(pageCount - 2)
    items.add(pageCount - 3)
  }

  const sorted = [...items].filter((item) => item >= 1 && item <= pageCount).sort((a, b) => a - b)
  const output: PaginationItem[] = []

  for (const item of sorted) {
    const previous = output.at(-1)
    if (typeof previous === 'number' && item - previous > 1) {
      output.push('ellipsis')
    }
    output.push(item)
  }

  return output
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden
      className={cn('size-4', direction === 'left' ? 'rotate-180' : null)}
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="m6 3.5 4.5 4.5L6 12.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

export function Pagination({
  disabled = false,
  labels,
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  total,
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(page, 1), pageCount)
  const items = getPaginationItems(safePage, pageCount)

  return (
    <div className="flex flex-col items-stretch gap-3 py-3 sm:flex-row sm:items-center sm:justify-end">
      <div className="flex items-center justify-end gap-1">
        <IconButton
          aria-label={labels.previous}
          disabled={disabled || safePage <= 1}
          onClick={() => {
            onPageChange(safePage - 1)
          }}
          size="sm"
        >
          <ChevronIcon direction="left" />
        </IconButton>
        {items.map((item, index) =>
          item === 'ellipsis' ? (
            <span
              aria-hidden
              className="flex size-8 items-center justify-center text-sm text-[var(--color-text-muted)]"
              key={`ellipsis-${String(index)}`}
            >
              ...
            </span>
          ) : (
            <button
              aria-current={item === safePage ? 'page' : undefined}
              className={cn(
                'flex size-8 cursor-pointer items-center justify-center rounded text-sm transition-colors',
                item === safePage
                  ? 'border border-[var(--color-primary)] text-[var(--color-primary)]'
                  : 'text-[var(--color-text)] hover:bg-[var(--color-hover-muted)]',
              )}
              disabled={disabled}
              key={item}
              onClick={() => {
                onPageChange(item)
              }}
              type="button"
            >
              {item}
            </button>
          ),
        )}
        <IconButton
          aria-label={labels.next}
          disabled={disabled || safePage >= pageCount}
          onClick={() => {
            onPageChange(safePage + 1)
          }}
          size="sm"
        >
          <ChevronIcon direction="right" />
        </IconButton>
      </div>
      <div className="w-[104px] shrink-0 self-end sm:self-auto">
        <Select
          aria-label={labels.pageSize(pageSize)}
          className="h-8"
          disabled={disabled}
          onValueChange={(value) => {
            onPageSizeChange(Number(value))
          }}
          options={pageSizeOptions.map((size) => ({
            label: labels.pageSize(size),
            value: String(size),
          }))}
          value={String(pageSize)}
        />
      </div>
    </div>
  )
}
